import axios, { type AxiosError, type InternalAxiosRequestConfig, isAxiosError } from "axios"
import {
  resolveBrowserApiBaseUrl,
  resolveServerApiBaseUrl,
  resolveTenantSlugFromHost,
  usesDirectBackendApi,
} from "@/lib/api-base-url"
import { captureApiRequest, captureSuccessfulMutation } from "@/lib/analytics"
import { selectedBranchId, clearSelectedBranch, activeBranchLabel, BRANCH_HOLD_MESSAGE } from "@/lib/branch-selection"

function resolveBaseURL() {
  if (typeof window !== "undefined") return resolveBrowserApiBaseUrl()
  return resolveServerApiBaseUrl()
}

const api = axios.create({
  baseURL: resolveBaseURL(),
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
})

const requestStartedAt = new WeakMap<InternalAxiosRequestConfig, number>()

function trackCompletedRequest(config: InternalAxiosRequestConfig | undefined, status: number): void {
  if (!config) return
  const startedAt = requestStartedAt.get(config)
  if (startedAt === undefined) return
  requestStartedAt.delete(config)
  captureApiRequest(config.method, config.url, status, performance.now() - startedAt)
}

const MOBILE_ACCESS_TOKEN_KEY = "pos_mobile_access_token"
const MOBILE_REFRESH_TOKEN_KEY = "pos_mobile_refresh_token"

/** Mobile browsers use bearer auth because cross-site cookies are unreliable there. */
export function isMobileBrowser(): boolean {
  if (typeof window === "undefined") return false
  const ua = window.navigator.userAgent
  return /android|iphone|ipad|ipod|iemobile|opera mini|mobile/i.test(ua) ||
    (/macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1)
}

function readMobileToken(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

export function getMobileRefreshToken(): string | null {
  if (typeof window === "undefined") return null
  return readMobileToken(MOBILE_REFRESH_TOKEN_KEY)
}

export function saveMobileTokens(accessToken: string, refreshToken: string) {
  if (typeof window === "undefined") throw new Error("Mobile browser storage is unavailable.")
  try {
    window.sessionStorage.setItem(MOBILE_ACCESS_TOKEN_KEY, accessToken)
    window.sessionStorage.setItem(MOBILE_REFRESH_TOKEN_KEY, refreshToken)
  } catch {
    clearMobileTokens()
    throw new Error("This browser does not allow session storage. Enable it to sign in.")
  }
}

export function clearMobileTokens() {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.removeItem(MOBILE_ACCESS_TOKEN_KEY)
    window.sessionStorage.removeItem(MOBILE_REFRESH_TOKEN_KEY)
  } catch {
    // Storage may be disabled by the browser.
  }
}

api.interceptors.request.use((config) => {
  requestStartedAt.set(config, performance.now())
  if (typeof window !== "undefined") {
    if (/\/api\/auth\/(login|logout|register)$/.test(config.url ?? "")) clearSelectedBranch()
    const branchId = selectedBranchId()
    const hasBranchHeader = Boolean(config.headers.get("X-Branch-Id"))
    if (branchId && !hasBranchHeader && !/\/api\/auth\/(login|logout|register|refresh)(\/|$|\?)/.test(config.url ?? "")) {
      config.headers.set("X-Branch-Id", branchId)
    }
    const method = (config.method ?? "get").toLowerCase()
    const url = config.url ?? ""
    const writing = method !== "get" && method !== "head"
    const allowed = /\/api\/(auth|branches|settings|admin|sessions|tenants|qz|clients|suppliers|variants|stock|workers|invoices|purchase-orders|returns|exchanges|payments|client-payments|restaurant|reports|pos\/(terminals|shifts|held-bills|tokens))(\/|$|\?)/.test(url)
    if (writing && activeBranchLabel()?.secondary && !allowed) {
      return Promise.reject(new Error(BRANCH_HOLD_MESSAGE))
    }
  }
  if (isMobileBrowser()) {
    config.withCredentials = false
    config.headers.set("X-Auth-Mode", "bearer")
    const accessToken = readMobileToken(MOBILE_ACCESS_TOKEN_KEY)
    if (accessToken && !/\/api\/auth\/(login|register)$/.test(config.url ?? "")) {
      config.headers.set("Authorization", `Bearer ${accessToken}`)
    }
  }
  if (usesDirectBackendApi()) {
    const slug = resolveTenantSlugFromHost()
    if (slug) {
      config.headers.set("x-tenant-slug", slug)
    }
  }
  return config
})

let failedQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = []
let inFlightRefresh: Promise<void> | null = null

function processQueue(error: unknown) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve()
  })
  failedQueue = []
}

/** One shared refresh so tab-focus, interceptors, and timers cannot rotate in parallel. */
export function refreshSession(): Promise<void> {
  if (!inFlightRefresh) {
    const mobile = isMobileBrowser()
    const refreshToken = mobile ? getMobileRefreshToken() : null
    inFlightRefresh = api
      .post("/api/auth/refresh", mobile ? { refreshToken } : undefined)
      .then(({ data }) => {
        if (mobile && data?.accessToken && data?.refreshToken) {
          saveMobileTokens(data.accessToken, data.refreshToken)
        }
      })
      .catch((error) => {
        if (mobile && isAuthError(error)) clearMobileTokens()
        throw error
      })
      .finally(() => {
        inFlightRefresh = null
      })
  }
  return inFlightRefresh
}

export function isNetworkError(error: unknown) {
  return isAxiosError(error) && !error.response
}

export function isAuthError(error: unknown) {
  return isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)
}

export function isTransientHttpError(error: unknown) {
  if (!isAxiosError(error)) return false
  const status = error.response?.status
  return status === 429 || (typeof status === "number" && status >= 500)
}

export function isSessionRevokedError(error: unknown) {
  if (!isAxiosError(error)) return false
  const data = error.response?.data as { sessionRevoked?: boolean } | undefined
  return data?.sessionRevoked === true
}

/** JWT valid but user row missing / inactive — common after DB reset while cookies remain. */
export function isInvalidAccountSession(error: unknown) {
  if (!isAxiosError(error)) return false
  const url = error.config?.url ?? ""
  if (url.includes("/api/auth/login") || url.includes("/api/auth/register")) return false
  const status = error.response?.status
  const msg = (error.response?.data as { message?: string } | undefined)?.message ?? ""
  if (status === 404 && url.includes("/api/auth/me") && msg === "User not found") return true
  if (
    status === 403 &&
    (msg === "Account is inactive or not found" ||
      msg === "Access denied: account does not belong to this workspace")
  ) {
    return true
  }
  return false
}

export function getApiError(error: unknown, fallback = "An error occurred"): string {
  if (isAxiosError<{ message?: string; error?: string; errors?: Array<{ message?: string; path?: Array<string | number> }> }>(error) && error.response?.data) {
    const status = error.response.status ?? 0
    const data = error.response.data

    // Never surface raw server / Prisma dumps in the UI
    if (status >= 500) {
      const msg = data.message?.trim()
      if (msg && msg.length <= 120 && !looksLikeInternalError(msg)) return msg
      return fallback === "An error occurred" ? "Something went wrong. Please try again." : fallback
    }

    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = data.errors[0]!
      if (first.message && first.message !== "Validation failed") return first.message
      const path = first.path?.filter((p) => p !== "lines" && typeof p !== "number").join(".")
      if (path && first.message) return `${path}: ${first.message}`
      if (first.message) return first.message
    }

    const msg = data.message ?? data.error
    if (msg != null && !looksLikeInternalError(msg)) {
      return typeof msg === "string" ? msg : String(msg)
    }
    return fallback
  }
  if (error instanceof Error) {
    if (looksLikeInternalError(error.message)) return fallback
    return error.message
  }
  return fallback
}

function looksLikeInternalError(message: unknown): boolean {
  const text = typeof message === "string" ? message : message == null ? "" : String(message)
  const m = text.toLowerCase()
  return (
    text.length > 180 ||
    m.includes("prisma") ||
    m.includes("invalid `") ||
    m.includes("invocation") ||
    m.includes("\n") ||
    m.includes("at handleRequestError")
  )
}

/** 401 from auth middleware — request never reached the route handler; safe to retry after refresh. */
function isSessionExpired401(error: AxiosError) {
  const msg = (error.response?.data as { message?: string } | undefined)?.message ?? ""
  return (
    msg === "Access token required"
    || msg === "Invalid or expired access token"
  )
}

api.interceptors.response.use(
  (response) => {
    trackCompletedRequest(response.config, response.status)
    captureSuccessfulMutation(response.config.method, response.config.url, response.config.data, response.data)
    return response
  },
  async (error: AxiosError) => {
    trackCompletedRequest(error.config, error.response?.status ?? 0)
    const held = error.response?.data as { code?: string; message?: string } | undefined
    if (held?.code === "BRANCH_WORKFLOW_NOT_ENABLED") held.message = BRANCH_HOLD_MESSAGE
    const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined

    const resData = error.response?.data as { sessionRevoked?: boolean; message?: string } | undefined
    if (resData?.sessionRevoked === true && typeof window !== "undefined") {
      clearMobileTokens()
      window.dispatchEvent(
        new CustomEvent("session:revoked", {
          detail: { message: resData.message || "Your session was revoked by an administrator." },
        }),
      )
    }

    if (isInvalidAccountSession(error) && typeof window !== "undefined") {
      clearMobileTokens()
      window.dispatchEvent(
        new CustomEvent("session:revoked", {
          detail: {
            message:
              "Your sign-in session is no longer valid (for example after a database reset).",
          },
        }),
      )
    }

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    // Only auto-refresh for expired/missing access tokens — not login failures or revoked refresh.
    if (!isSessionExpired401(error)) {
      return Promise.reject(error)
    }

    const url = originalRequest.url ?? ""
    if (url.includes("/api/auth/refresh") || url.includes("/api/auth/login")) {
      return Promise.reject(error)
    }

    if (inFlightRefresh) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve: () => resolve(api(originalRequest)), reject })
      })
    }

    originalRequest._retry = true

    try {
      await refreshSession()
      processQueue(null)
      return api(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError)
      return Promise.reject(refreshError)
    }
  },
)

export default api
